import { useState, useMemo, useCallback } from 'react';
import { Video, AudioFeatures } from '@meradio/shared-types';

export interface FilterOptions {
  searchQuery: string;
  channelId: string;
  bpmRange: {
    min: number;
    max: number;
  };
  keySignature: number | null;
  mode: number | null;
  minValence: number | null;
  minEnergy: number | null;
}

export interface ExtraSort {
  options: Array<{ value: string; label: string }>;
  compare: (a: Video, b: Video, sortBy: string) => number | null;
}

export interface UseVideoFiltersResult {
  filters: FilterOptions;
  setFilters: React.Dispatch<React.SetStateAction<FilterOptions>>;
  resetFilters: () => void;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  filteredVideos: Video[];
}

export const DEFAULT_FILTERS: FilterOptions = {
  searchQuery: '',
  channelId: '',
  bpmRange: { min: 60, max: 200 },
  keySignature: null,
  mode: null,
  minValence: null,
  minEnergy: null,
};

export function useVideoFilters(
  videos: Video[],
  audioFeatures: Record<string, AudioFeatures>,
  extraSort?: ExtraSort
): UseVideoFiltersResult {
  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState<string>('recent');
  const [showFilters, setShowFilters] = useState(false);

  const filteredVideos = useMemo(() => {
    const filtered = videos.filter((video) => {
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        if (
          !video.title.toLowerCase().includes(query) &&
          !video.description?.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      if (filters.channelId && video.channelId !== filters.channelId) {
        return false;
      }

      const features = audioFeatures[video.videoId];
      if (features) {
        if (features.tempo < filters.bpmRange.min || features.tempo > filters.bpmRange.max) {
          return false;
        }

        if (filters.keySignature !== null && features.key !== filters.keySignature) {
          return false;
        }

        if (filters.mode !== null && features.mode !== filters.mode) {
          return false;
        }

        if (filters.minValence !== null && (features.valence ?? 0) < filters.minValence) {
          return false;
        }

        if (filters.minEnergy !== null && (features.energy ?? 0) < filters.minEnergy) {
          return false;
        }
      } else if (
        filters.keySignature !== null ||
        filters.mode !== null ||
        filters.minValence !== null ||
        filters.minEnergy !== null
      ) {
        return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        case 'oldest':
          return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime();
        case 'title':
          return a.title.localeCompare(b.title);
        default: {
          if (extraSort) {
            const result = extraSort.compare(a, b, sortBy);
            if (result !== null) {
              return result;
            }
          }
          return 0;
        }
      }
    });

    return filtered;
  }, [videos, audioFeatures, filters, sortBy, extraSort]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return {
    filters,
    setFilters,
    resetFilters,
    sortBy,
    setSortBy,
    showFilters,
    setShowFilters,
    filteredVideos,
  };
}
